import React, { memo, useCallback, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { Box, Typography } from '@mui/material'
import { Banner, BannerType } from '@finanzcheck/teal-components'
import Text from 'components/Input/Text'
import { useStores } from 'stores/utils/hooks/useStores'
import { Error } from './Error'
import { getRemainingAuthAttempts, subscribePrefillStatus } from './utils'
import { AutoFillType } from 'utils/autofill'
import { PrefillErrorKind } from 'stores/PrefillStore'
import maskPhoneNumber from './maskPhoneNumber'

export const PIN_LENGTH = 6 // digits
const PIN_PLACEHOLDER = 'z.B. 345901'

const AuthenticatePin: React.FC = observer(() => {
  const {
    prefill: {
      pin,
      setPin,
      isPinInvalid,
      setIsPinInvalid,
      isError,
      setIsError,
      isPinVerified,
      setErrorKind,
      isPhoneMobile,
      pinType
    },
    traversal: {
      data: {
        debtors: {
          primary: {
            contact: { phoneNumber },
            personal: { firstname, lastname }
          }
        }
      }
    }
  } = useStores()

  const onPasteHandler = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault()
      setIsPinInvalid(false)
      const pasted = event.clipboardData.getData('text')
      const cleaned = pasted.replace(/\s+/g, '').replace(/\D/g, '')

      // Make sure we don't exceed the PIN length
      const trimmed = cleaned.slice(0, PIN_LENGTH)

      setPin(trimmed)
    },
    [setPin, setIsPinInvalid]
  )

  const handlePinChange = useCallback(
    (newPin: string) => {
      const cleanedPin = newPin.replace(/\s+/g, '').replace(/\D/g, '')
      setIsPinInvalid(false)
      setPin(cleanedPin)
    },
    [setPin, setIsPinInvalid]
  )

  const [remainingAttempts, setRemainingAttempts] = useState(
    getRemainingAuthAttempts()
  )

  useEffect(() => {
    const unsubscribe = subscribePrefillStatus(() => {
      setRemainingAttempts(getRemainingAuthAttempts())
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    if (remainingAttempts === 0 && !isPinVerified) {
      setIsError(true)
      setErrorKind(PrefillErrorKind.MAX_ATTEMPTS)
    }
  }, [remainingAttempts, setErrorKind, setIsError])

  const inputError = isPinInvalid
    ? `Die eingegebene PIN ist nicht korrekt oder ist abgelaufen. Bitte versuchen Sie es erneut oder fordern Sie eine neue PIN an. Sie haben noch ${remainingAttempts} Eingabeversuche übrig.`
    : undefined

  if (isError) {
    return <Error />
  }

  return (
    <div>
      <Typography
        variant="h4"
        sx={{ marginBlock: '16px' }}
      >{`Willkommen zurück, ${firstname} ${lastname}!`}</Typography>
      <Box marginBottom={2} marginTop={2}>
        <Banner
          type={BannerType.SUCCESS}
          desc={
            isPhoneMobile && pinType === 'sms'
              ? `PIN an ${maskPhoneNumber(phoneNumber)} gesendet`
              : `PIN an Ihre E-Mail-Adresse gesendet`
          }
          isIconVersionOutline
        />
      </Box>

      <Typography variant="body1" paragraph>
        Bitte geben Sie unten Ihre PIN ein. Diese PIN läuft nach 4 Minuten ab.
      </Typography>

      <Box mb={2}>
        <Text
          value={pin}
          onPaste={onPasteHandler}
          onChange={handlePinChange}
          type="tel"
          autoComplete={AutoFillType.ONE_TIME_CODE}
          placeholder={PIN_PLACEHOLDER}
          acceptedInput={/[0-9]/}
          error={inputError}
          outerLabel
          smallTextField
          showTooltipOnFieldClick
          inputProps={{
            maxLength: PIN_LENGTH,
            inputMode: 'numeric'
          }}
        />
      </Box>
    </div>
  )
})

export default memo(AuthenticatePin)
